import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { getRoleDashboardPath } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required")
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: LoginForm) => {
      const response = await apiRequest("POST", "/auth/login", data);
      return response.json();
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch the auth query to update the authentication state
      await queryClient.invalidateQueries({ queryKey: ['/auth/me'] });
      
      // Wait for the query to refetch to ensure user data is available
      await queryClient.refetchQueries({ queryKey: ['/auth/me'] });
      
      // Redirect to role-specific dashboard based on user role from login response
      const userRole = data?.user?.role;
      const dashboardPath = userRole ? getRoleDashboardPath(userRole) : '/';
      setLocation(dashboardPath);
    },
    onError: (error: any) => {
      toast({
        title: "Login Failed",
        description: error.message || "Login failed. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: LoginForm) => {
    loginMutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 font-muli">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 relative">
        <CardHeader className="text-center space-y-2 pt-16">
          {/* Back Button */}
          <div className="absolute top-4 left-4">
            <Button
              variant="ghost"
              onClick={() => setLocation("/")}
              className="text-servify-purple hover:text-servify-purple-dark hover:bg-servify-purple/10"
            >
              ← Back to Home
            </Button>
          </div>
          <CardTitle className="text-3xl font-bold text-servify-purple">
            Welcome Back
          </CardTitle>
          <CardDescription className="text-gray-600">
            Sign in to your Servify LaunchPad account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email"
                        className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium">Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Enter your password"
                        className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Forgot Password Link */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setLocation("/forgot-password")}
                  className="text-sm text-servify-purple hover:text-servify-purple-dark font-medium underline hover:no-underline transition-colors"
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loginMutation.isPending}
                className="w-full h-12 bg-servify-purple hover:bg-servify-purple-dark text-white font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {loginMutation.isPending ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </Form>


          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <button className="text-servify-purple hover:text-servify-purple-dark font-medium underline">
                Contact your administrator
              </button>
            </p>
          </div>

        </CardContent>
      </Card>
    </div>
  );
}
